import * as Popover from "@radix-ui/react-popover";
import { ChevronDown, Search } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

export type CustomDropdownOption = {
  label: string;
  value: string;
};

type CustomDropdownProps = {
  size?: "md" | "lg";
  options: CustomDropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  searchable?: boolean;
  textSize?: "xs" | "sm";
  disabled?: boolean;
  ariaLabel?: string;
};

export default function CustomDropdown({
  options,
  size = "md",
  value,
  onChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  searchable = false,
  textSize = "sm",
  disabled = false,
  ariaLabel = "Select option",
}: CustomDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [contentWidth, setContentWidth] = useState<number | undefined>(
    undefined
  );
  const triggerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  useEffect(() => {
    if (open && triggerRef.current) {
      setContentWidth(triggerRef.current.offsetWidth);
    }
    if (!open) {
      setSearch("");
    }
  }, [open]);

  // Focus the search input as soon as the popover opens (only when searchable)
  useEffect(() => {
    if (open && searchable) {
      // Use a short timeout to allow Radix to finish mounting
      const id = setTimeout(() => searchRef.current?.focus(), 10);
      return () => clearTimeout(id);
    }
  }, [open, searchable]);

  const filteredOptions = search.trim()
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(search.toLowerCase())
      )
    : options;

  const selectedLabel =
    options.find((opt) => opt.value === value)?.label ?? placeholder;

  const handleSelect = (optionValue: string) => {
    if (disabled) return;
    onChange(optionValue);
    setOpen(false);
  };

  return (
    <div className="w-full min-w-0">
      <Popover.Root
        open={disabled ? false : open}
        onOpenChange={(nextOpen) => !disabled && setOpen(nextOpen)}
      >
        <Popover.Trigger asChild>
          <div className="w-full min-w-0">
            <div
              ref={triggerRef}
              style={size === "lg" ? { minHeight: 44 } : undefined}
              role="combobox"
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-controls={listboxId}
              aria-label={ariaLabel}
              aria-disabled={disabled}
              tabIndex={disabled ? -1 : 0}
              className={`flex min-h-8 w-full min-w-0 items-center justify-between rounded-lg border border-secondary-6 px-3 py-1.5 text-left shadow-sm transition-[border-color,box-shadow] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-6/40 ${
                disabled
                  ? "cursor-not-allowed bg-gray-300 opacity-60"
                  : "cursor-pointer bg-white hover:border-primary-5/50 hover:shadow"
              }`}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setOpen((prev) => !prev);
                }
                if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                  e.preventDefault();
                  setOpen(true);
                }
                if (e.key === "Escape") setOpen(false);
              }}
            >
              <span
                className={
                  value
                    ? `text-${textSize} font-medium text-primary-1`
                    : `text-${textSize} text-secondary-3`
                }
              >
                {selectedLabel}
              </span>
              <span
                className={`flex shrink-0 items-center transition-transform duration-200 ${open ? "rotate-180" : ""}`}
              >
                <ChevronDown className="size-4 text-secondary-3" />
              </span>
            </div>
          </div>
        </Popover.Trigger>

        <Popover.Content
          className="z-50 overflow-hidden rounded-lg border border-secondary-6 bg-white shadow-lg"
          side="bottom"
          sideOffset={6}
          align="start"
          style={
            contentWidth != null
              ? { width: `${contentWidth}px`, minWidth: `${contentWidth}px` }
              : undefined
          }
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            triggerRef.current?.focus();
          }}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            if (!searchable) {
              const list = listRef.current;
              const selected = list?.querySelector<HTMLElement>(
                '[aria-selected="true"]'
              );
              (
                selected || list?.querySelector<HTMLElement>('[role="option"]')
              )?.focus();
            }
          }}
        >
          {/* Search input (only when searchable) */}
          {searchable && (
            <div className="flex items-center gap-2 border-b border-secondary-6 px-3 py-2">
              <Search className="size-3.5 shrink-0 text-secondary-3" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className={`w-full bg-transparent text-${textSize} text-primary-1 placeholder:text-secondary-3 focus:outline-none`}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setOpen(false);
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    listRef.current
                      ?.querySelector<HTMLElement>('[role="option"]')
                      ?.focus();
                  }
                }}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="shrink-0 text-secondary-3 hover:text-primary-1"
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          )}

          {/* Options list */}
          <ul
            id={listboxId}
            ref={listRef}
            role="listbox"
            className="m-0 max-h-52 list-none overflow-y-auto py-1"
            onKeyDown={(e) => {
              if (e.key === "Escape") setOpen(false);
              if (["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) {
                e.preventDefault();
                const options = Array.from(
                  e.currentTarget.querySelectorAll<HTMLElement>(
                    '[role="option"]'
                  )
                );
                const current = options.indexOf(
                  document.activeElement as HTMLElement
                );
                const next =
                  e.key === "Home"
                    ? 0
                    : e.key === "End"
                      ? options.length - 1
                      : (current +
                          (e.key === "ArrowDown" ? 1 : -1) +
                          options.length) %
                        options.length;
                options[next]?.focus();
              }
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <li
                  key={opt.value}
                  role="option"
                  tabIndex={-1}
                  style={size === "lg" ? { minHeight: 44 } : undefined}
                  aria-selected={opt.value === value}
                  className={`cursor-pointer px-3 py-2.5 text-${textSize} transition-colors hover:bg-primary-4 focus:bg-primary-4 focus:outline-none ${
                    opt.value === value
                      ? "bg-primary-4 font-semibold text-primary-6"
                      : "text-primary-1"
                  }`}
                  onClick={() => handleSelect(opt.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSelect(opt.value);
                    }
                  }}
                >
                  {opt.label}
                </li>
              ))
            ) : (
              <li className={`px-3 py-2.5 text-${textSize} text-secondary-3`}>
                {search.trim() ? (
                  <>No results for &ldquo;{search}&rdquo;</>
                ) : (
                  "No options available"
                )}
              </li>
            )}
          </ul>
        </Popover.Content>
      </Popover.Root>
    </div>
  );
}
